/**
 * Was an die API geht und was der Browser selbst tun muss.
 *
 * Die Katalogseite hat bis hierher **alles** selbst getan: alle Seiten geholt,
 * lokal gefiltert, lokal sortiert. Mit seitenweiser Blaetterung
 * (argus-platform #193) geht das nicht mehr -- wer nur 50 Zeilen im Speicher hat
 * und lokal sortiert, sortiert die sichtbaren 50 und nicht die 1977. Das sieht
 * plausibel aus und ist falsch, und niemand meldet es.
 *
 * Deshalb steht die Grenze hier, an einer Stelle, mit Tests: welche Sortierung die
 * API kann, welche nur der Browser kennt, und wann die Seite den vollen Satz
 * braucht, weil ihre Filter ueber IDs laufen, die die API nicht kennt.
 */

/**
 * Spalte der Tabelle → Sortierfeld der API (camelCase, die API uebersetzt selbst).
 *
 * Was hier fehlt, ist in der Tabelle kein Schalter: die API weist ein unbekanntes
 * Feld mit 400 ab, statt es stillschweigend zu verwerfen (#192). Die Gegenprobe
 * dazu steht im Backend (`test_die_vier_spalten_der_tabelle_sind_alle_sortierbar`).
 */
export const API_SORTIERFELDER = {
  name: 'localName',
  frequency: 'caseCoverage',
  omopStatus: 'omopStatus',
  sensitivity: 'sensitivity',
};

/**
 * Sortierungen, die die API nicht kennen kann: Listenzugehoerigkeit steht in den
 * Arbeitslisten des Nutzers, nicht im Katalog.
 *
 * `openehrStatus` steht seit Runde 3 hier (Review Runde 2, Blocker B1): die
 * Kopfzeile bot die Spalte in Stellung openEHR bereits als sortierbar an, aber
 * das Feld stand in keiner der beiden Listen -- `apiOrdering` gab '' zurueck,
 * die lokale Sortierung (`getSortValue` in `useFilterSort.js`) kannte den
 * Namen nicht und gab fuer jede Zeile 0 zurueck. Der Klick sah aktiv aus
 * (Spaltenkopf `active`), sortierte aber nichts -- und verwarf zusaetzlich
 * eine zuvor aktive Sortierung. Ob die API `openehr_status` als Sortierfeld
 * je erlauben wird, ist von hier aus nicht zu klaeren (Backend #770 nicht
 * lesbar) -- bis dahin bleibt die Sortierung bewusst lokal: langsamer bei
 * grossen Ergebnismengen (Vollsatz-Modus, siehe `brauchtVollsatz`), aber
 * korrekt.
 */
export const LOKALE_SORTIERFELDER = ['lists', 'listA', 'listB', 'openehrStatus'];

export const SEITENGROESSEN = [25, 50, 100];
export const SEITENGROESSE_VORGABE = 50;

/**
 * Die Obergrenze der IDs, die die Seite in einem `ids=`-Filter anfragt.
 *
 * Runde 1 hat diese Zahl als Kopie von `MAX_IDS` (argus-platform #195,
 * Backend, dort **zweimal** geaendert) eingetragen -- exakt 839. Review-Runde
 * 1, Blocker B3: eine kopierte Zahl laueft auseinander, sobald sich die
 * Backend-Rechnung wieder aendert, und die Richtung des Fehlers ist die
 * gefaehrliche: sinkt `MAX_IDS` im Backend, ueberschaetzt eine stehen
 * gebliebene Kopie die Grenze und schickt genau die Anfrage, die AK3
 * ausschliesst (414/400 statt sichtbarem Vollsatz-Rueckfall).
 *
 * Der Worktree eines Bau-Agenten enthaelt nur dieses eine Repo (#277) -- es
 * gibt weder eine dokumentierte Konfigurationsschnittstelle, ueber die sich
 * `MAX_IDS` zur Bau- oder Laufzeit lesen liesse, noch Zugriff auf das
 * Backend-Repo, um dort eine zu schaffen. Eine exakte Kopie ist von hier aus
 * darum nicht verlaesslich herstellbar.
 *
 * Diese Konstante ist deshalb bewusst **kein** Versuch, die Backend-Zahl zu
 * treffen, sondern eine bewusst niedrige Sicherheitsschwelle deutlich
 * darunter: faellt eine Anfrage auf den Vollsatz zurueck, obwohl das Backend
 * tatsaechlich noch mehr IDs erlaubt haette, ist das langsamer, aber korrekt.
 * Ueberschaetzt die Schwelle die tatsaechliche Backend-Grenze, waere das
 * genau der Fehler, den AK3 ausschliesst -- darum die grosse Marge. Der
 * Test dazu (`katalogBlaetterung.test.js`) sichert diese Richtung: die
 * Konstante muss unterhalb der zuletzt bekannten Backend-Zahl (839) bleiben,
 * nicht nur "irgendeine positive Zahl" sein.
 *
 * Volle Behebung -- die Zahl aus der API lesen, statt sie zu raten -- braucht
 * einen Backend-Endpunkt, der `MAX_IDS` traegt. Das ist aus diesem Worktree
 * heraus nicht baubar; siehe `## Annahmen` im MR.
 */
export const MAX_IDS_FILTER = 500;

/**
 * Der `ordering`-Parameter fuer die API -- oder '' , wenn die API nicht sortiert.
 */
export function apiOrdering(orderBy, order) {
  const feld = API_SORTIERFELDER[orderBy];
  if (!feld) return '';
  return order === 'desc' ? `-${feld}` : feld;
}

/**
 * Braucht die Seite den **vollen** Satz statt einer Seite oder eines
 * `ids=`-Filters?
 *
 * Seit #277 nur noch die Faelle, fuer die es keinen Filter gibt:
 *
 * - der Vergleich zweier Arbeitslisten, **solange die Zeilen "in keiner der
 *   beiden Listen" (Chip "neither") oder "alle" (Chip "all") sichtbar sein
 *   sollen** -- "neither" ist eine Ausschluss-Menge, die sich nicht als
 *   `ids=`-Filter ausdruecken laesst. "all" **sieht** wie eine Teilmenge der
 *   Vereinigung aus, ist es aber nicht: "alle" heisst "jede Zeile, die die
 *   uebrigen Filter trifft", nicht "jede Zeile in einer der beiden Listen" --
 *   Runde 2 hat genau diese beiden verwechselt (Review Runde 2, NEU-B5: der
 *   Chip zeigte/zaehlte nur noch die Vereinigung statt des ganzen Katalogs,
 *   "Alle (5)" neben "Keine (1972)"). Nur die verbleibenden zwei Zustaende
 *   -- list1Only, list2Only, both -- sind echte Teilmengen der
 *   **Vereinigung** beider Listen und filtern ueber `ids=`, siehe
 *   `idsFuerListenvergleich` weiter unten;
 * - die Arbeitsliste "ungeprüfte KI-Vorschlaege" (#257 AK4) -- sie filtert
 *   nach Herkunft eines Feldes, nicht nach einer bekannten ID-Menge;
 * - eine Sortierung, die nur der Browser kennt (Listenzugehoerigkeit).
 *
 * KI-Vorschlaege, ein geladenes Set und "nur Parameter der aktiven Liste"
 * filtern seit #277 stattdessen ueber `ids=` -- siehe `idsFuerFilter` und
 * `MAX_IDS_FILTER` weiter oben in dieser Datei.
 */
export function brauchtVollsatz({
  activeWorkingList = [],
  orderBy = '',
  // Die Arbeitsliste aus #257 AK4 ("was die KI vorgeschlagen und niemand
  // geprueft hat") filtert ueber die Herkunft -- ein Feld, das die API nicht
  // als Suchparameter kennt. Ohne den vollen Satz waere das Ergebnis nur die
  // Schnittmenge mit der sichtbaren Seite.
  onlyUnvalidatedAiSuggestions = false,
  // Der gewaehlte Chip im Listenvergleich ('all' | 'list1Only' | 'list2Only'
  // | 'both' | 'neither'). 'neither' und 'all' verlangen den Vollsatz --
  // siehe Doku oben.
  listComparisonFilter = 'all',
  // Der Archetyp-Chip (#788): die API kennt kein Filterfeld dafuer -- dieselbe
  // Lage wie bei LOKALE_SORTIERFELDER, nur fuer einen Filter statt eine
  // Sortierung. Ein erfundener Query-Parameter koennte das Backend mit 400
  // abweisen und die ganze Liste mitreissen (#247); die lokale Filterung in
  // useFilterSort ist deshalb bewusst der sicherere Weg, siehe MR.
  // `null` heisst "kein Chip gewaehlt" -- **nicht** `''`: der Bucket "ohne
  // Zuordnung" ist selbst ein waehlbarer Chip mit dem Wert `''` (leerer
  // `openehrName`), der sich so von "nichts gewaehlt" unterscheiden laesst.
  selectedArchetype = null,
} = {}) {
  if (
    activeWorkingList.length === 2 &&
    (listComparisonFilter === 'neither' || listComparisonFilter === 'all')
  ) return true;
  if (LOKALE_SORTIERFELDER.includes(orderBy)) return true;
  if (onlyUnvalidatedAiSuggestions) return true;
  if (selectedArchetype !== null) return true;
  return false;
}

/**
 * Welche IDs die Seite statt eines Vollsatzes anfragen kann (#277) -- oder
 * `null`, wenn keiner der drei Faelle aktiv ist.
 *
 * KI-Vorschlaege und ein geladenes Set kombinieren sich ueber die
 * Vereinigung -- dieselbe Regel, die `useFilterSort` bisher lokal nachbaute
 * ("einer von beiden reicht", siehe `matchesAiOrSetFilter` dort).
 * "Nur Parameter der aktiven Liste" schneidet das Ergebnis danach
 * zusaetzlich auf die aktive Liste zu (UND, nicht ODER) -- exakt die
 * Kombination aus `matchesAiOrSetFilter && matchesActiveListFilter`.
 *
 * Das Ergebnis kann eine **leere** Liste sein (z. B. KI-Vorschlaege, von
 * denen keiner in der aktiven Arbeitsliste steht) -- das ist ein gueltiges
 * "null Treffer", kein Grund, den Filter zu ignorieren. Eine leere Liste darf
 * darum nie unveraendert als `ids=`-Parameter verschickt werden: die API
 * sieht einen fehlenden `ids`-Parameter nicht anders als einen leeren und
 * liefert dann den ganzen Katalog zurueck. Diesen Fall faengt die aufrufende
 * Seite ab (DataCatalog.js), nicht diese Funktion.
 */
export function idsFuerFilter({
  aiSuggestedParameterIds = [],
  setParameterIds = [],
  showOnlyActiveListParams = false,
  // Die IDs der aktiven Arbeitsliste, wenn genau eine aktiv ist. Bei zwei
  // aktiven Listen gilt statt dieser Funktion `idsFuerListenvergleich` weiter
  // unten -- diese Funktion muss den Fall darum nicht kennen.
  activeListParameterIds = [],
} = {}) {
  const hatAiOderSetFilter = aiSuggestedParameterIds.length > 0 || setParameterIds.length > 0;
  if (!hatAiOderSetFilter && !showOnlyActiveListParams) return null;

  let ids = hatAiOderSetFilter
    ? [...new Set([...aiSuggestedParameterIds, ...setParameterIds])]
    : [...activeListParameterIds];

  if (showOnlyActiveListParams && hatAiOderSetFilter) {
    ids = ids.filter(id => activeListParameterIds.includes(id));
  }

  return ids;
}

/**
 * Die IDs fuer den Vergleich zweier Arbeitslisten (#277, Runde 2, Blocker B4)
 * -- die Vereinigung beider Listen.
 *
 * Nur die Chips "nur A", "nur B" und "beide" zeigen ausschliesslich Zeilen,
 * die in mindestens einer der beiden Listen stehen -- jede davon ist eine
 * Teilmenge dieser Vereinigung, filterbar ueber `ids=`. "alle" und "keine von
 * beiden" ("neither") bleiben beim Vollsatz (siehe `brauchtVollsatz`):
 * "neither" ist eine Ausschluss-Menge, die sich nicht als `ids=`-Filter
 * ausdruecken laesst, und "alle" heisst "jede Zeile, die die uebrigen Filter
 * trifft" -- das schliesst Zeilen ein, die in keiner der beiden Listen
 * stehen, ist also **keine** Teilmenge dieser Vereinigung (Review Runde 2,
 * NEU-B5).
 *
 * Anders als bei `idsFuerFilter` ist eine leere Vereinigung hier kein Sonderfall
 * fuer die aufrufende Seite: zwei leere Listen sind ein gueltiges "null
 * Treffer" wie jede andere leere ID-Menge auch (siehe `idsFuerFilter`-Doku).
 */
export function idsFuerListenvergleich({ list1ParameterIds = [], list2ParameterIds = [] } = {}) {
  return [...new Set([...list1ParameterIds, ...list2ParameterIds])];
}

/**
 * Die letzte Seite -- mindestens 1, damit eine leere Liste nicht auf Seite 0 landet.
 */
export function letzteSeite(gesamtzahl, seitengroesse) {
  if (!Number.isFinite(gesamtzahl) || gesamtzahl <= 0) return 1;
  return Math.max(1, Math.ceil(gesamtzahl / seitengroesse));
}

/**
 * Ein Schluessel, der sich genau dann aendert, wenn die Ergebnismenge eine andere
 * ist -- dann muss die Blaetterung zurueck auf Seite 1.
 *
 * Warum als Zeichenkette und nicht als Abhaengigkeitsliste: die Werte sind Arrays,
 * und ein neues Array mit gleichem Inhalt ist fuer `useEffect` eine Aenderung. Ohne
 * diesen Schluessel springt die Seite bei jedem Rendern auf 1 zurueck.
 */
export function ergebnisSchluessel({
  q = '',
  tags = [],
  stations = [],
  sensitivity = '',
  omopStatus = '',
  sourceSystem = [],
  ordering = '',
  pageSize = SEITENGROESSE_VORGABE,
  // Der Archetyp-Chip (#788) aendert die Ergebnismenge, ohne dass sich einer
  // der uebrigen Werte hier aendert -- ohne ihn bliebe die Blaetterung nach
  // einem Chip-Klick auf der zuvor angezeigten Seitenzahl stehen.
  archetype = null,
} = {}) {
  return JSON.stringify([
    q,
    [...tags].sort(),
    [...stations].sort(),
    sensitivity,
    omopStatus,
    [...sourceSystem].sort(),
    ordering,
    pageSize,
    archetype,
  ]);
}
